import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import { io, iot, mqtt } from "aws-iot-device-sdk-v2";

export interface SoundData {
    Lp_bands: number[];
    // add other properties as needed
}

interface MqttContextType {
    dBA: number | null;
    thirdOctaveData: number[];
    connectionStatus: 'connected' | 'disconnected' | 'error';
    error: boolean;
}

const MqttContext = createContext<MqttContextType>({
    dBA: null,
    thirdOctaveData: [],
    connectionStatus: 'disconnected',
    error: false,
});

interface MqttProviderProps {
    topicPrefix: string;
    children: React.ReactNode;
}

export const MqttProvider: React.FC<MqttProviderProps> = ({ topicPrefix, children }) => {
    const [dBA, setDba] = useState<number | null>(null);
    const [thirdOctaveData, setThirdOctaveData] = useState<number[]>([]);
    const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'error'>('disconnected');
    const [error, setError] = useState<boolean>(false);
    const [connection, setConnection] = useState<mqtt.MqttClientConnection | null>(null);
    const [reconnectAttempts, setReconnectAttempts] = useState(0);
    const MAX_RECONNECT_ATTEMPTS = 3;

    // Establish MQTT connection
    const setupConnection = useCallback(async () => {
        try {
            const clientBootstrap = new io.ClientBootstrap();
            const configBuilder = iot.AwsIotMqttConnectionConfigBuilder.new_with_websockets();
            configBuilder.with_credentials(
                process.env.REACT_APP_AWS_REGION || 'us-east-2',
                process.env.REACT_APP_AWS_ACCESS_KEY_ID,
                process.env.REACT_APP_AWS_SECRET_ACCESS_KEY
            );
            configBuilder.with_clean_session(true);
            configBuilder.with_client_id(`eremos_${Date.now()}`);
            configBuilder.with_endpoint("a2phsstdmda0n0-ats.iot.us-east-2.amazonaws.com");
            const config = configBuilder.build();
            const client = new mqtt.MqttClient(clientBootstrap);
            const mqttConnection = client.new_connection(config);
            await mqttConnection.connect();
            setConnection(mqttConnection);
            setConnectionStatus('connected');
            return mqttConnection;
        } catch (err) {
            console.error("Connection error:", err);
            setConnectionStatus('error');
            throw err;
        }
    }, []);

    const subscribeToTopic = useCallback(
        async (mqttConnection: mqtt.MqttClientConnection) => {
            try {
                await mqttConnection.subscribe(
                    `${topicPrefix}/soundData`,
                    mqtt.QoS.AtLeastOnce,
                    (topic: string, payload: ArrayBuffer) => {
                        const decoder = new TextDecoder("utf-8");
                        const payloadString = decoder.decode(payload);
                        try {
                            const data = JSON.parse(payloadString) as SoundData;
                            if (data.Lp_bands?.length > 0) {
                                const latestdBA = data.Lp_bands[data.Lp_bands.length - 1];
                                setDba(latestdBA);
                                setThirdOctaveData(data.Lp_bands);
                            }
                        } catch (err) {
                            console.error("Error parsing MQTT message:", err);
                        }
                    }
                );
            } catch (err) {
                console.error("Subscription error:", err);
                throw err;
            }
        },
        [topicPrefix]
    );

    const sendStartCommand = useCallback(
        async (mqttConnection: mqtt.MqttClientConnection) => {
            try {
                const message = JSON.stringify({ command: "start" });
                await mqttConnection.publish(
                    `${topicPrefix}/control`,
                    message,
                    mqtt.QoS.AtLeastOnce
                );
            } catch (err) {
                console.error("Error sending start command:", err);
            }
        },
        [topicPrefix]
    );

    const handleReconnect = useCallback(async () => {
        if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
            setReconnectAttempts(prev => prev + 1);
            try {
                const newConnection = await setupConnection();
                await subscribeToTopic(newConnection);
            } catch (err) {
                setTimeout(handleReconnect, 5000);
            }
        } else {
            setError(true);
        }
    }, [reconnectAttempts, setupConnection, subscribeToTopic]);

    useEffect(() => {
        let currentConnection: mqtt.MqttClientConnection | null = null;
        const initializeConnection = async () => {
            try {
                currentConnection = await setupConnection();
                await sendStartCommand(currentConnection);
                await subscribeToTopic(currentConnection);
            } catch (err) {
                handleReconnect();
            }
        };

        initializeConnection();

        return () => {
            if (currentConnection) {
                currentConnection.disconnect()
                    .then(() => {

                        setConnectionStatus('disconnected');
                    })
                    .catch(err => console.error("Disconnect error:", err));
            }
        };
    }, [setupConnection, subscribeToTopic, sendStartCommand, handleReconnect]);

    return (
        <MqttContext.Provider value={{ dBA, thirdOctaveData, connectionStatus, error }}>
            {children}
        </MqttContext.Provider>
    );
};

export const useMqtt = () => useContext(MqttContext);
